import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  IconButton,
  FormControlLabel,
  Grid,
  MenuItem,
  Pagination,
  Paper,
  Select,
  Switch,
  TextField,
  Typography
} from '@mui/material';
import StorefrontIcon from '@mui/icons-material/Storefront';
import SaveIcon from '@mui/icons-material/Save';
import CollectionsOutlinedIcon from '@mui/icons-material/CollectionsOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import SearchIcon from '@mui/icons-material/Search';
import { MapContainer, Marker, Popup, TileLayer, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import {
  getRestaurantSettings,
  saveRestaurantSettings,
  updateRestaurantBranch,
} from '../../../services/ecommerceApi';
import ImageLibrarySelectionModal from '../../accounting/inventory/ImageLibrarySelectionModal';

interface BranchSettingsComponentProps {
  companyId: string | null;
  configId: string;
  onSettingsChange?: (settings: any) => void;
}

/**
 * Şube ayarları paneli (Branches e-ticaret görünüm yönetimi).
 *
 * Şube modülü tamamen `Branches` tablosundan çalışır.
 * Bu component yalnızca şubelerin e-ticaret görünüm alanlarını
 * (görsel, harita linki, sıra, e-ticarette göster) düzenler.
 * Şube ekleme/silme `Branches` yönetim sayfasından yapılır.
 */
interface BranchRow {
  id: string;
  branchId: number;
  city: string;
  district?: string;
  branchName: string;
  address?: string;
  phone?: string;
  imageUrl?: string;
  mapUrl?: string;
  displayOrder: number;
  showOnEcommerce: boolean;
}

interface BranchPageSettings {
  heroEnabled: boolean;
  heroTitle: string;
  heroDescription: string;
  heroBackgroundColor: string;
}

interface MapSearchResultItem {
  lat: number;
  lng: number;
  label: string;
}

interface MapPointItem {
  id: string;
  name: string;
  city: string;
  lat: number;
  lng: number;
}

delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-shadow.png',
});

const parseCoordsFromGoogleMapsUrl = (raw?: string): { lat: number; lng: number } | null => {
  const value = String(raw || '').trim();
  if (!value) return null;

  const decoded = (() => {
    try {
      return decodeURIComponent(value);
    } catch {
      return value;
    }
  })();

  const atMatch = decoded.match(/@(-?\d+(?:\.\d+)?),\s*(-?\d+(?:\.\d+)?)/i);
  if (atMatch) {
    const lat = Number(atMatch[1]);
    const lng = Number(atMatch[2]);
    if (Number.isFinite(lat) && Number.isFinite(lng)) return { lat, lng };
  }

  const qMatch = decoded.match(/[?&](?:q|query|ll)=(-?\d+(?:\.\d+)?),\s*(-?\d+(?:\.\d+)?)/i);
  if (qMatch) {
    const lat = Number(qMatch[1]);
    const lng = Number(qMatch[2]);
    if (Number.isFinite(lat) && Number.isFinite(lng)) return { lat, lng };
  }

  const dMatch = decoded.match(/!3d(-?\d+(?:\.\d+)?)!4d(-?\d+(?:\.\d+)?)/i);
  if (dMatch) {
    const lat = Number(dMatch[1]);
    const lng = Number(dMatch[2]);
    if (Number.isFinite(lat) && Number.isFinite(lng)) return { lat, lng };
  }

  return null;
};

const toGoogleMapsPointUrl = (lat: number, lng: number): string =>
  `https://www.google.com/maps?q=${lat.toFixed(6)},${lng.toFixed(6)}`;

const normalizeMapUrlInput = (value?: string): string => {
  const raw = String(value || '').trim();
  if (!raw) return '';

  if (/^https?:\/\//i.test(raw)) return raw;
  if (/^www\./i.test(raw)) return `https://${raw}`;
  if (/^maps\.app\.goo\.gl\//i.test(raw)) return `https://${raw}`;
  if (/^[A-Za-z0-9_-]{10,}$/.test(raw)) return `https://maps.app.goo.gl/${raw}`;

  return raw;
};

const normalizeColorHex = (value?: string): string => {
  const raw = String(value || '').trim();
  return /^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/.test(raw) ? raw : '#f8fafc';
};

const normalizeTr = (value?: string): string =>
  String(value || '')
    .toLocaleLowerCase('tr')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim();

const splitTokens = (value?: string): string[] =>
  normalizeTr(value)
    .split(/[^a-z0-9çğıöşü]+/i)
    .map((t) => t.trim())
    .filter((t) => t.length >= 3);

const STOP_TOKENS = new Set([
  'sube',
  'subesi',
  'şube',
  'şubesi',
  'restaurant',
  'restoran',
  'branch',
  'merkez',
  'center',
  'no',
]);

const DISTRICT_OR_CITY_CENTER: Record<string, { lat: number; lng: number }> = {
  'istanbul': { lat: 41.0082, lng: 28.9784 },
  'ankara': { lat: 39.9334, lng: 32.8597 },
  'bursa': { lat: 40.195, lng: 29.06 },
  'duzce': { lat: 40.8438, lng: 31.1565 },
  'düzce': { lat: 40.8438, lng: 31.1565 },
  'bolu': { lat: 40.7395, lng: 31.6116 },
  'kocaeli': { lat: 40.7667, lng: 29.9167 },
  'mugla': { lat: 37.2153, lng: 28.3636 },
  'muğla': { lat: 37.2153, lng: 28.3636 },
  'sivas': { lat: 39.7477, lng: 37.0179 },
  'yalova': { lat: 40.655, lng: 29.2769 },
  'sakarya': { lat: 40.7569, lng: 30.3781 },
  'adapazari': { lat: 40.7569, lng: 30.3781 },
  'adapazarı': { lat: 40.7569, lng: 30.3781 },
  'maltepe': { lat: 40.9351, lng: 29.1557 },
  'basaksehir': { lat: 41.0931, lng: 28.8020 },
  'başakşehir': { lat: 41.0931, lng: 28.8020 },
  'merkez': { lat: 39.0, lng: 35.0 },
};

const resolveDistrictCityCenter = (district?: string, city?: string): { lat: number; lng: number } => {
  const districtKey = normalizeTr(district);
  const cityKey = normalizeTr(city);
  const isGenericDistrict = districtKey === 'merkez' || districtKey === 'center';
  if (!isGenericDistrict && DISTRICT_OR_CITY_CENTER[districtKey]) {
    return DISTRICT_OR_CITY_CENTER[districtKey];
  }
  return DISTRICT_OR_CITY_CENTER[cityKey] || DISTRICT_OR_CITY_CENTER[districtKey] || { lat: 39.0, lng: 35.0 };
};

const MapClickHandler: React.FC<{ onPick: (lat: number, lng: number) => void }> = ({ onPick }) => {
  const map = useMap();

  useEffect(() => {
    const handleClick = (e: any) => {
      onPick(e.latlng.lat, e.latlng.lng);
    };

    map.on('click', handleClick);
    return () => {
      map.off('click', handleClick);
    };
  }, [map, onPick]);

  return null;
};

const FitBounds: React.FC<{ points: Array<{ lat: number; lng: number }> }> = ({ points }) => {
  const map = useMap();

  useEffect(() => {
    const canMoveMap = Boolean((map as any)?._loaded) && Boolean((map as any)?._mapPane);
    if (!canMoveMap) {
      return;
    }

    if (!points.length) {
      map.setView([39.0, 35.0], 6, { animate: false });
      return;
    }

    if (points.length === 1) {
      map.setView([points[0].lat, points[0].lng], 15, { animate: false });
      return;
    }

    const bounds = (L as any).latLngBounds(points.map((p) => [p.lat, p.lng] as [number, number]));
    map.fitBounds(bounds.pad(0.2), { maxZoom: 14, animate: false });
  }, [map, points]);

  return null;
};

const RecenterMap: React.FC<{ lat: number; lng: number }> = ({ lat, lng }) => {
  const map = useMap();

  useEffect(() => {
    const canMoveMap = Boolean((map as any)?._loaded) && Boolean((map as any)?._mapPane);
    if (!canMoveMap) {
      return;
    }
    map.setView([lat, lng], Math.max(map.getZoom(), 13), { animate: false });
  }, [map, lat, lng]);

  return null;
};

const BranchSettingsComponent: React.FC<BranchSettingsComponentProps> = ({
  companyId,
  configId,
  onSettingsChange,
}) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [branches, setBranches] = useState<BranchRow[]>([]);
  const [savingRow, setSavingRow] = useState<number | null>(null);
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(12);
  const [searchTerm, setSearchTerm] = useState('');
  const [imageModalOpen, setImageModalOpen] = useState(false);
  const [imageTargetRowId, setImageTargetRowId] = useState<number | null>(null);
  const [mapPickerOpen, setMapPickerOpen] = useState(false);
  const [mapTargetRowId, setMapTargetRowId] = useState<number | null>(null);
  const [mapDraftCoords, setMapDraftCoords] = useState<{ lat: number; lng: number } | null>(null);
  const [mapSearchQuery, setMapSearchQuery] = useState('');
  const [mapSearchLoading, setMapSearchLoading] = useState(false);
  const [mapSearchError, setMapSearchError] = useState<string | null>(null);
  const [mapSearchResults, setMapSearchResults] = useState<MapSearchResultItem[]>([]);
  const [branchPageSettings, setBranchPageSettings] = useState<BranchPageSettings>({
    heroEnabled: true,
    heroTitle: 'Şubeler',
    heroDescription: 'Şehirlere göre şubelerimizi keşfedin.',
    heroBackgroundColor: '#f8fafc',
  });
  const [savingPageSettings, setSavingPageSettings] = useState(false);

  const activeCities = useMemo(() => {
    const set = new Set<string>();
    branches.forEach((x) => {
      if (x.showOnEcommerce && x.city?.trim()) set.add(x.city.trim());
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b, 'tr'));
  }, [branches]);

  const filteredBranches = useMemo(() => {
    const q = searchTerm.trim().toLocaleLowerCase('tr');
    if (!q) return branches;
    return branches.filter((b) =>
      [
        b.branchName,
        b.city,
        b.district,
        b.address,
        b.phone
      ].some((field) => String(field || '').toLocaleLowerCase('tr').includes(q))
    );
  }, [branches, searchTerm]);

  const totalPages = Math.max(1, Math.ceil(filteredBranches.length / rowsPerPage));
  const imageTargetRow = useMemo(
    () => branches.find((x) => x.branchId === imageTargetRowId) || null,
    [branches, imageTargetRowId]
  );
  const mapTargetRow = useMemo(
    () => branches.find((x) => x.branchId === mapTargetRowId) || null,
    [branches, mapTargetRowId]
  );
  const previewMapPoints = useMemo<MapPointItem[]>(
    () =>
      filteredBranches
        .filter((row) => row.showOnEcommerce)
        .map((row) => {
          const coords = parseCoordsFromGoogleMapsUrl(normalizeMapUrlInput(row.mapUrl));
          return {
            id: row.id,
            name: row.branchName,
            city: row.city,
            lat: (coords || resolveDistrictCityCenter(row.district, row.city)).lat,
            lng: (coords || resolveDistrictCityCenter(row.district, row.city)).lng,
          };
        })
        .filter((x): x is MapPointItem => x !== null),
    [filteredBranches]
  );
  const pagedBranches = useMemo(() => {
    const start = (page - 1) * rowsPerPage;
    return filteredBranches.slice(start, start + rowsPerPage);
  }, [filteredBranches, page, rowsPerPage]);

  useEffect(() => {
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [page, totalPages]);

  useEffect(() => {
    setPage(1);
  }, [searchTerm]);

  const loadData = async () => {
    if (!companyId || !configId) return;
    try {
      setLoading(true);
      const response = await getRestaurantSettings(companyId, configId);
      if (response?.success && response?.data) {
        const data = response.data;
        setBranchPageSettings({
          heroEnabled: data.heroEnabled !== false,
          heroTitle: String(data.heroTitle || 'Şubeler'),
          heroDescription: String(data.heroDescription || 'Şehirlere göre şubelerimizi keşfedin.'),
          heroBackgroundColor: normalizeColorHex(data.heroBackgroundColor),
        });
        const rawLocations: any[] = Array.isArray(data.locations) ? data.locations : [];
        const mapped: BranchRow[] = rawLocations.map((b) => ({
          id: String(b.id ?? ''),
          branchId: Number(b.branchId ?? 0),
          city: b.city || '',
          district: b.district || '',
          branchName: b.branchName || '',
          address: b.address || '',
          phone: b.phone || '',
          imageUrl: b.imageUrl || '',
          mapUrl: b.mapUrl || '',
          displayOrder: Number(b.displayOrder ?? 0),
          showOnEcommerce: b.showOnEcommerce === true,
        }));
        setBranches(mapped);
        setError(null);
        if (onSettingsChange) onSettingsChange(data);
      }
    } catch (e) {
      console.error(e);
      setError('Şube ayarları yüklenemedi.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadData();
  }, [companyId, configId]);

  const updateBranchField = (branchId: number, patch: Partial<BranchRow>) => {
    setBranches((prev) => prev.map((x) => (x.branchId === branchId ? { ...x, ...patch } : x)));
  };

  const savePageSettings = async () => {
    if (!configId) return;
    try {
      setSavingPageSettings(true);
      setError(null);
      await saveRestaurantSettings({
        configId,
        heroEnabled: branchPageSettings.heroEnabled,
        heroTitle: branchPageSettings.heroTitle,
        heroDescription: branchPageSettings.heroDescription,
        heroBackgroundColor: normalizeColorHex(branchPageSettings.heroBackgroundColor),
      });
      setSuccess('Şube sayfası görünüm ayarları kaydedildi.');
      setTimeout(() => setSuccess(null), 2000);
      if (onSettingsChange) {
        onSettingsChange({
          heroEnabled: branchPageSettings.heroEnabled,
          heroTitle: branchPageSettings.heroTitle,
          heroDescription: branchPageSettings.heroDescription,
          heroBackgroundColor: branchPageSettings.heroBackgroundColor,
        });
      }
    } catch (e) {
      console.error(e);
      const apiMessage =
        (e as any)?.response?.data?.message ||
        (e as any)?.response?.data?.Message ||
        (e as any)?.message;
      setError(apiMessage ? `Şube sayfası ayarları kaydedilemedi: ${apiMessage}` : 'Şube sayfası ayarları kaydedilemedi.');
    } finally {
      setSavingPageSettings(false);
    }
  };

  const saveBranchRow = async (row: BranchRow) => {
    if (!row.branchId) return;
    try {
      setSavingRow(row.branchId);
      setError(null);
      const normalizedMapUrl = normalizeMapUrlInput(row.mapUrl);
      const mapUrlToSave: string | null = normalizedMapUrl || null;

      await updateRestaurantBranch(String(row.branchId), {
        imageUrl: row.imageUrl?.trim() || null,
        mapUrl: mapUrlToSave,
        displayOrder: Number(row.displayOrder || 0),
        showOnEcommerce: row.showOnEcommerce,
      });
      setSuccess(`"${row.branchName}" şubesi güncellendi.`);
      setTimeout(() => setSuccess(null), 2000);
    } catch (e) {
      console.error(e);
      setError('Şube güncellenemedi.');
    } finally {
      setSavingRow(null);
    }
  };

  const openImageLibrary = (branchId: number) => {
    setImageTargetRowId(branchId);
    setImageModalOpen(true);
  };

  const closeImageLibrary = () => {
    setImageModalOpen(false);
    setImageTargetRowId(null);
  };

  const handleImageLibrarySave = (selectedUrls: string[]) => {
    if (!imageTargetRowId) return;
    updateBranchField(imageTargetRowId, { imageUrl: selectedUrls[0] || '' });
    closeImageLibrary();
  };

  const openMapPicker = (row: BranchRow) => {
    const parsed = parseCoordsFromGoogleMapsUrl(row.mapUrl);
    setMapTargetRowId(row.branchId);
    setMapDraftCoords(parsed || { lat: 39.0, lng: 35.0 });
    setMapSearchQuery([row.branchName, row.district, row.city, 'Türkiye'].filter(Boolean).join(' '));
    setMapSearchError(null);
    setMapSearchResults([]);
    setMapPickerOpen(true);
  };

  const closeMapPicker = () => {
    setMapPickerOpen(false);
    setMapTargetRowId(null);
    setMapDraftCoords(null);
    setMapSearchQuery('');
    setMapSearchError(null);
    setMapSearchLoading(false);
    setMapSearchResults([]);
  };

  const saveMapLocation = () => {
    if (!mapTargetRowId || !mapDraftCoords) return;
    updateBranchField(mapTargetRowId, {
      mapUrl: toGoogleMapsPointUrl(mapDraftCoords.lat, mapDraftCoords.lng),
    });
    closeMapPicker();
  };

  const searchLocationOnMap = async (overrideQuery?: string) => {
    const query = (overrideQuery ?? mapSearchQuery).trim();
    if (!query) {
      setMapSearchError('Lütfen aranacak bir konum yazın.');
      return;
    }

    try {
      setMapSearchLoading(true);
      setMapSearchError(null);
      setMapSearchResults([]);

      const branchName = String(mapTargetRow?.branchName || '').trim();
      const city = String(mapTargetRow?.city || '').trim();
      const district = String(mapTargetRow?.district || '').trim();
      const address = String(mapTargetRow?.address || '').trim();
      const branchNameTokens = splitTokens(branchName).filter((t) => !STOP_TOKENS.has(t));
      const queryTokens = splitTokens(query);
      const looksLikeBrandSearch =
        queryTokens.length > 0 &&
        queryTokens.every((qt) => branchNameTokens.some((bt) => bt.includes(qt) || qt.includes(bt)));

      const candidates = Array.from(new Set([
        // Önce seçili şube bağlamı:
        [branchName, address, district, city, 'Türkiye'].filter(Boolean).join(', '),
        [address, district, city, 'Türkiye'].filter(Boolean).join(', '),
        [branchName, district, city, 'Türkiye'].filter(Boolean).join(', '),
        // Sonra kullanıcının serbest sorgusu:
        query,
        [query, district, city, 'Türkiye'].filter(Boolean).join(', '),
        [query, city, 'Türkiye'].filter(Boolean).join(', '),
        [branchName, district, city, 'Türkiye'].filter(Boolean).join(', '),
        [address, district, city, 'Türkiye'].filter(Boolean).join(', '),
      ].filter(Boolean)));

      const allHits: Array<{ lat: number; lng: number; label: string }> = [];

      for (const candidate of candidates) {
        const q = encodeURIComponent(candidate);
        const response = await fetch(
          `https://nominatim.openstreetmap.org/search?format=jsonv2&addressdetails=1&limit=6&countrycodes=tr&accept-language=tr&q=${q}`
        );
        const data = await response.json();
        const rows = Array.isArray(data) ? data : [];

        rows.forEach((hit: any) => {
          const lat = Number(hit?.lat);
          const lng = Number(hit?.lon);
          const label = String(hit?.display_name || '').trim();
          if (Number.isFinite(lat) && Number.isFinite(lng) && label) {
            allHits.push({ lat, lng, label });
          }
        });
      }

      const uniqueMap = new Map<string, { lat: number; lng: number; label: string }>();
      allHits.forEach((item) => {
        const key = `${item.lat.toFixed(6)}|${item.lng.toFixed(6)}`;
        if (!uniqueMap.has(key)) uniqueMap.set(key, item);
      });

      const scoreOf = (label: string): number => {
        const lower = label.toLocaleLowerCase('tr');
        let score = 0;
        if (city && lower.includes(city.toLocaleLowerCase('tr'))) score += 5;
        if (district && lower.includes(district.toLocaleLowerCase('tr'))) score += 4;
        if (address) {
          const addrTokens = splitTokens(address).filter((t) => !STOP_TOKENS.has(t));
          addrTokens.forEach((t) => {
            if (lower.includes(t)) score += 2;
          });
        }
        branchNameTokens.forEach((t) => {
          if (lower.includes(t)) score += 3;
        });
        if (lower.includes('türkiye') || lower.includes('turkiye')) score += 1;
        return score;
      };

      const ranked = Array.from(uniqueMap.values())
        .sort((a, b) => scoreOf(b.label) - scoreOf(a.label))
        .slice(0, 12)
        .map((x) => ({ lat: x.lat, lng: x.lng, label: x.label }));

      const cityNorm = normalizeTr(city);
      const districtNorm = normalizeTr(district);
      const filteredByContext = ranked.filter((r) => {
        const labelNorm = normalizeTr(r.label);
        if (districtNorm && labelNorm.includes(districtNorm)) return true;
        if (cityNorm && labelNorm.includes(cityNorm)) return true;
        return false;
      });

      const strictBranchResults = ranked.filter((r) => {
        const labelNorm = normalizeTr(r.label);
        if (!looksLikeBrandSearch) return false;
        const hasBranchToken = branchNameTokens.some((t) => labelNorm.includes(t));
        const hasGeoContext =
          (districtNorm && labelNorm.includes(districtNorm)) ||
          (cityNorm && labelNorm.includes(cityNorm));
        return hasBranchToken && hasGeoContext;
      });

      const finalResults =
        strictBranchResults.length > 0
          ? strictBranchResults
          : filteredByContext.length > 0
            ? filteredByContext
            : ranked;

      if (finalResults.length === 0) {
        setMapSearchError('Konum bulunamadı. Daha açık bir adres/şube adı deneyin.');
        return;
      }

      setMapSearchResults(finalResults.slice(0, 8));
      setMapDraftCoords({ lat: finalResults[0].lat, lng: finalResults[0].lng });
    } catch (error) {
      console.error('Map search failed:', error);
      setMapSearchError('Arama sırasında hata oluştu. Tekrar deneyin.');
    } finally {
      setMapSearchLoading(false);
    }
  };

  if (loading) {
    return <Box sx={{ py: 4 }}><Typography>Yükleniyor...</Typography></Box>;
  }

  return (
    <Paper elevation={3} sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center' }}>
          <StorefrontIcon sx={{ mr: 1 }} />
          Şube Görünümü
        </Typography>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      <Paper variant="outlined" sx={{ p: 1.5, mb: 2 }}>
        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>
          Şube Sayfası Bölüm Ayarları
        </Typography>
        <Box sx={{ mb: 1 }}>
          <FormControlLabel
            control={
              <Switch
                checked={branchPageSettings.heroEnabled}
                onChange={(e) => setBranchPageSettings((prev) => ({ ...prev, heroEnabled: e.target.checked }))}
              />
            }
            label="Üst Başlık Alanını Göster (Şubeler / açıklama)"
            sx={{ m: 0 }}
          />
        </Box>

        {branchPageSettings.heroEnabled && (
          <Grid container spacing={2} sx={{ mb: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                label="Başlık"
                value={branchPageSettings.heroTitle}
                onChange={(e) =>
                  setBranchPageSettings((prev) => ({ ...prev, heroTitle: e.target.value }))
                }
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                multiline
                minRows={2}
                label="Açıklama"
                value={branchPageSettings.heroDescription}
                onChange={(e) =>
                  setBranchPageSettings((prev) => ({ ...prev, heroDescription: e.target.value }))
                }
              />
            </Grid>
            <Grid item xs={12} md={8}>
              <TextField
                fullWidth
                size="small"
                label="Arkaplan Rengi"
                value={branchPageSettings.heroBackgroundColor}
                onChange={(e) =>
                  setBranchPageSettings((prev) => ({ ...prev, heroBackgroundColor: e.target.value }))
                }
                placeholder="#f8fafc"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                size="small"
                type="color"
                label="Renk Seçici"
                value={normalizeColorHex(branchPageSettings.heroBackgroundColor)}
                onChange={(e) =>
                  setBranchPageSettings((prev) => ({ ...prev, heroBackgroundColor: e.target.value }))
                }
                InputLabelProps={{ shrink: true }}
                sx={{
                  '& input': {
                    p: 0.5,
                    height: 38,
                  },
                }}
              />
            </Grid>
          </Grid>
        )}

        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant="contained"
            size="small"
            startIcon={<SaveIcon />}
            onClick={() => void savePageSettings()}
            disabled={savingPageSettings}
          >
            {savingPageSettings ? 'Kaydediliyor...' : 'Kaydet'}
          </Button>
        </Box>
      </Paper>

      <Typography sx={{ mb: 2 }}>Şube Görünümü ({branches.length})</Typography>
      <Paper variant="outlined" sx={{ p: 1.5, mb: 2 }}>
        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>
          Harita Önizleme ({previewMapPoints.length} konum)
        </Typography>
        <Box sx={{ height: 320, borderRadius: 1, overflow: 'hidden', border: '1px solid', borderColor: 'divider' }}>
          <MapContainer
            center={[39.0, 35.0]}
            zoom={6}
            style={{ height: '100%', width: '100%' }}
            key={`preview-${previewMapPoints.length}`}
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            <FitBounds points={previewMapPoints} />
            {previewMapPoints.map((p) => (
              <Marker key={`${p.id}-${p.lat}-${p.lng}`} position={[p.lat, p.lng]}>
                <Popup>
                  <strong>{p.name}</strong><br />
                  {p.city}
                </Popup>
              </Marker>
            ))}
          </MapContainer>
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          Not: Exact link çözülemezse önizlemede ilçe/şehir merkezi işaretlenir.
        </Typography>
      </Paper>
      <TextField
        fullWidth
        size="small"
        label="Şube Ara"
        placeholder="Şube adı, şehir, ilçe, adres veya telefon..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        sx={{ mb: 1.5, maxWidth: 520 }}
      />

      {branches.length === 0 && (
            <Alert severity="warning">
              Henüz hiç şube yok. Şubeler sayfasından yeni şube ekleyin; ardından bu ekranda
              e-ticaret görünümünü düzenleyebilirsiniz.
            </Alert>
      )}

      {branches.length > 0 && (
        <Box sx={{ mb: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Typography variant="caption" color="text.secondary">
            {filteredBranches.length === 0
              ? `0 / ${branches.length} şube`
              : `${(page - 1) * rowsPerPage + 1} - ${Math.min(page * rowsPerPage, filteredBranches.length)} / ${filteredBranches.length} şube`}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="caption" color="text.secondary">Sayfa başına:</Typography>
            <Select
              size="small"
              value={rowsPerPage}
              onChange={(e) => {
                const next = Number(e.target.value);
                setRowsPerPage(next);
                setPage(1);
              }}
              sx={{ minWidth: 78 }}
            >
              {[8, 12, 20, 30].map((size) => (
                <MenuItem key={size} value={size}>{size}</MenuItem>
              ))}
            </Select>
          </Box>
        </Box>
      )}

      {pagedBranches.map((row) => (
            <Paper key={row.branchId} variant="outlined" sx={{ p: 1.25, mb: 1 }}>
              <Grid container spacing={1} alignItems="center">
                <Grid item xs={12} md={3.5}>
                  <Box>
                    <Typography variant="subtitle2" fontWeight="bold">{row.branchName}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {[row.city, row.district].filter(Boolean).join(' / ') || '—'}
                    </Typography>
                    {Boolean(row.address) && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }} noWrap>
                        {row.address}
                      </Typography>
                    )}
                    {Boolean(row.phone) && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        Tel: {row.phone}
                      </Typography>
                    )}
                  </Box>
                </Grid>

                <Grid item xs={12} md={5}>
                  <Grid container spacing={1}>
                    <Grid item xs={12} md={6}>
                      <Box
                        sx={{
                          height: 40,
                          border: '1px solid',
                          borderColor: 'divider',
                          borderRadius: 1,
                          px: 1,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          gap: 1
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                          {row.imageUrl ? (
                            <Box
                              component="img"
                              src={row.imageUrl}
                              alt={row.branchName}
                              sx={{ width: 26, height: 26, borderRadius: 0.5, objectFit: 'cover', flexShrink: 0 }}
                            />
                          ) : (
                            <CollectionsOutlinedIcon sx={{ fontSize: 18, color: 'text.secondary', flexShrink: 0 }} />
                          )}
                          <Typography variant="caption" color="text.secondary" noWrap>
                            {row.imageUrl ? 'Görsel seçildi' : 'Görsel seçilmedi'}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                          {row.imageUrl && (
                            <IconButton
                              size="small"
                              onClick={() => updateBranchField(row.branchId, { imageUrl: '' })}
                              title="Görseli temizle"
                            >
                              <DeleteOutlineIcon fontSize="inherit" />
                            </IconButton>
                          )}
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => openImageLibrary(row.branchId)}
                            sx={{ minWidth: 0, px: 1 }}
                          >
                            Seç
                          </Button>
                        </Box>
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Harita Linki (Google Maps)"
                        value={row.mapUrl || ''}
                        onChange={(e) => updateBranchField(row.branchId, { mapUrl: e.target.value })}
                        placeholder="https://maps.google.com/... (konum linki)"
                        InputProps={{
                          endAdornment: row.mapUrl?.trim() ? (
                            <InputAdornment position="end">
                              <IconButton
                                size="small"
                                onClick={() => window.open(normalizeMapUrlInput(row.mapUrl), '_blank', 'noopener,noreferrer')}
                                title="Harita linkini yeni sekmede aç"
                              >
                                <OpenInNewIcon fontSize="inherit" />
                              </IconButton>
                            </InputAdornment>
                          ) : undefined
                        }}
                      />
                      <Box sx={{ mt: 0.5, display: 'flex', justifyContent: 'flex-end' }}>
                        <Button
                          size="small"
                          startIcon={<LocationOnIcon />}
                          onClick={() => openMapPicker(row)}
                        >
                          Haritadan Seç
                        </Button>
                      </Box>
                    </Grid>
                  </Grid>
                </Grid>

                <Grid item xs={4} md={1}>
                  <TextField
                    fullWidth
                    size="small"
                    type="number"
                    label="Sıra"
                    value={row.displayOrder}
                    onChange={(e) => updateBranchField(row.branchId, { displayOrder: Number(e.target.value || 0) })}
                  />
                </Grid>

                <Grid item xs={4} md={1.5}>
                  <FormControlLabel
                    control={
                      <Switch
                        size="small"
                        checked={row.showOnEcommerce}
                        onChange={(e) => updateBranchField(row.branchId, { showOnEcommerce: e.target.checked })}
                      />
                    }
                    label={row.showOnEcommerce ? 'Göster' : 'Gizli'}
                    sx={{ m: 0 }}
                  />
                </Grid>

                <Grid item xs={4} md={1} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<SaveIcon />}
                    disabled={savingRow === row.branchId}
                    onClick={() => void saveBranchRow(row)}
                    sx={{ minWidth: 0, px: 1.25 }}
                  >
                    {savingRow === row.branchId ? '...' : 'Kaydet'}
                  </Button>
                </Grid>
              </Grid>
            </Paper>
      ))}

      {filteredBranches.length === 0 && branches.length > 0 && (
        <Alert severity="info" sx={{ mt: 1 }}>
          Arama kriterine uygun şube bulunamadı.
        </Alert>
      )}

      {filteredBranches.length > rowsPerPage && (
        <Box sx={{ mt: 1.5, display: 'flex', justifyContent: 'center' }}>
          <Pagination
            size="small"
            page={page}
            count={totalPages}
            onChange={(_, val) => setPage(val)}
            color="primary"
          />
        </Box>
      )}

      {branches.length > 0 && (
        <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 0.75, alignItems: 'center' }}>
          <Typography variant="caption" color="text.secondary">Aktif şehirler:</Typography>
          {activeCities.length === 0 ? (
            <Typography variant="caption" color="text.secondary">—</Typography>
          ) : (
            activeCities.map((c) => <Chip key={c} size="small" label={c} />)
          )}
        </Box>
      )}

      <ImageLibrarySelectionModal
        open={imageModalOpen}
        onClose={closeImageLibrary}
        onSave={handleImageLibrarySave}
        companyId={companyId}
        productName={imageTargetRow?.branchName || 'Şube Görseli'}
        currentImageUrls={imageTargetRow?.imageUrl ? [imageTargetRow.imageUrl] : []}
        productId={imageTargetRow ? String(imageTargetRow.branchId) : null}
        singleSelect
        maxSelection={1}
      />

      <Dialog open={mapPickerOpen} onClose={closeMapPicker} maxWidth="md" fullWidth>
        <DialogTitle>
          Haritadan Konum Seç {mapTargetRow ? `- ${mapTargetRow.branchName}` : ''}
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            Haritaya tıklayarak şube konumunu işaretleyin. Kaydedince Google Maps linki otomatik oluşturulur.
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, mb: 1.25, alignItems: 'center' }}>
            <TextField
              fullWidth
              size="small"
              label="Konum Ara"
              placeholder="Örn: Düzce Hacıoğlu Şubesi"
              value={mapSearchQuery}
              onChange={(e) => setMapSearchQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  void searchLocationOnMap();
                }
              }}
            />
            <Button
              variant="outlined"
              startIcon={mapSearchLoading ? <CircularProgress size={14} /> : <SearchIcon />}
              onClick={() => void searchLocationOnMap()}
              disabled={mapSearchLoading}
            >
              Ara
            </Button>
            <Button
              variant="text"
              onClick={() => {
                const addrQuery = [mapTargetRow?.address, mapTargetRow?.district, mapTargetRow?.city, 'Türkiye']
                  .filter(Boolean)
                  .join(', ');
                setMapSearchQuery(addrQuery);
                void searchLocationOnMap(addrQuery);
              }}
              disabled={mapSearchLoading || !mapTargetRow?.address}
            >
              Adresten Bul
            </Button>
          </Box>
          {mapSearchError && (
            <Alert severity="warning" sx={{ mb: 1.25 }}>
              {mapSearchError}
            </Alert>
          )}
          {mapSearchResults.length > 0 && (
            <Box sx={{ mb: 1.25, maxHeight: 132, overflowY: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 0.5 }}>
              {mapSearchResults.map((result, index) => (
                <Button
                  key={`${result.lat}-${result.lng}-${index}`}
                  fullWidth
                  size="small"
                  variant={mapDraftCoords?.lat === result.lat && mapDraftCoords?.lng === result.lng ? 'contained' : 'text'}
                  onClick={() => setMapDraftCoords({ lat: result.lat, lng: result.lng })}
                  sx={{ justifyContent: 'flex-start', textTransform: 'none', mb: 0.25 }}
                >
                  {result.label}
                </Button>
              ))}
            </Box>
          )}
          <Box sx={{ height: 420, borderRadius: 1, overflow: 'hidden', border: '1px solid', borderColor: 'divider' }}>
            {mapDraftCoords && (
              <MapContainer
                center={[mapDraftCoords.lat, mapDraftCoords.lng]}
                zoom={mapTargetRow?.mapUrl ? 15 : 6}
                style={{ height: '100%', width: '100%' }}
              >
                <TileLayer
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                <RecenterMap lat={mapDraftCoords.lat} lng={mapDraftCoords.lng} />
                <Marker position={[mapDraftCoords.lat, mapDraftCoords.lng]} />
                <MapClickHandler onPick={(lat, lng) => setMapDraftCoords({ lat, lng })} />
              </MapContainer>
            )}
          </Box>
          {mapDraftCoords && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              Seçilen konum: {mapDraftCoords.lat.toFixed(6)}, {mapDraftCoords.lng.toFixed(6)}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeMapPicker}>Vazgeç</Button>
          <Button variant="contained" onClick={saveMapLocation} disabled={!mapDraftCoords}>
            Konumu Uygula
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default BranchSettingsComponent;
